<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import SettingService from '@/services/settings'

export default {
  page: {
    title: "Configurações",
    meta: [{ name: "description", content: appConfig.description }]
  },
  components: { Layout },
  data() {
    return {
      title: "Configurações",
      settings: {},
      companies: [],
      // uma entrada por marca (Calypso e Movix), vinda de /settings/app-version
      brands: [],
      fallbackMinVersion: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDate(date) {
      if (!date) {
        return '';
      }

      return new Date(date).toLocaleString('pt-BR');
    },
    // Rótulo do que está valendo hoje para a marca: é o que evita salvar um
    // bloqueio achando que o campo em branco não faz nada.
    effectiveMinVersion(brand, platform) {
      return brand.appVersion.minVersion[platform] || this.fallbackMinVersion || null;
    },
    async load() {
      let loader = this.$loading.show();

      try {
        const companiesResult = await SettingService.getCompanies();
        this.companies = companiesResult.data;

        const settingsResult = await SettingService.getSettings();
        this.settings = settingsResult.data;

        await this.loadAppVersion();

        loader.hide();
      } catch (err) {
        loader.hide();
        this.$toast.error('Ocorreu um erro ao carregar as configurações');
      }
    },
    async loadAppVersion() {
      const result = await SettingService.getAppVersionSettings();

      this.brands = result.data.companies;
      this.fallbackMinVersion = result.data.fallbackMinVersion;
    },
    async saveAppVersion(brand) {
      let loader = this.$loading.show();

      try {
        await SettingService.updateAppVersion(brand.company, brand.appVersion);
        await this.loadAppVersion();

        this.$toast.open(`Versão do ${brand.label} atualizada com sucesso`);

        loader.hide();
      } catch (err) {
        loader.hide();

        const msg = err.response && err.response.data && err.response.data.msg;
        this.$toast.error(msg || 'Ocorreu um erro ao salvar a configuração de versão');
      }
    },
    async save() {
      let loader = this.$loading.show();

      try {
        await SettingService.update({ companyCode: this.settings.companyCode });

        const settingsResult = await SettingService.getSettings();
        this.settings = settingsResult.data;

        this.$toast.open('Configuração atualizada com sucesso');

        loader.hide();
      } catch (err) {
        loader.hide();
        this.$toast.error('Ocorreu um erro ao salvar as configurações');
      }
    }
  },
};
</script>

<template>
  <Layout>
    <div class="row">
      <div class="col-12">
        <div class="page-title-box d-flex align-items-center justify-content-between">
          <h4 class="mb-0 font-size-18">{{title}}</h4>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <form>
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label for="title">Empresa Padrão</label>
                  <select class="custom-select" v-model="settings.companyCode">
                    <option v-for="company in companies" :key="company._id" :value="company.code">{{company.name}}</option>
                  </select>
              </div>
            </div>
          </div>

          <div class="row mt-2 align-items-center">
            <div class="col-sm-6">
              <button type="button" class="btn btn-primary btn-rounded mb-2" @click="save">
                <i class="mdi mdi-check mr-1"></i> Salvar Configuração
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="page-title-box d-flex align-items-center justify-content-between pb-0">
          <h4 class="mb-0 font-size-18">Versão do Aplicativo</h4>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body pb-2">
        <p class="text-muted mb-0 font-size-13">
          Cada marca tem o seu app nas lojas e pode estar em uma versão diferente, por isso o controle é separado.
          A <strong>atualização obrigatória</strong> bloqueia o app: quem está abaixo dela só sai indo para a loja.
          O <strong>aviso de versão nova</strong> o usuário pode dispensar.
        </p>
      </div>
    </div>

    <div class="card" v-for="brand in brands" :key="brand.company">
      <div class="card-body">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h5 class="card-title mb-0">{{brand.label}}</h5>
          <span class="badge badge-soft-secondary font-size-12" v-if="brand.appVersion.latestVersionUpdatedAt">
            Última consulta à App Store: {{formatDate(brand.appVersion.latestVersionUpdatedAt)}}
          </span>
        </div>

        <div class="alert alert-warning mb-3" v-if="!brand.exists">
          Esta marca ainda não tem configurações de B2B cadastradas no banco, então não há onde salvar a versão.
        </div>

        <fieldset :disabled="!brand.exists">
          <h6 class="text-uppercase text-muted font-size-11 mb-2">Atualização obrigatória</h6>

          <div class="row">
            <div class="col-md-3">
              <div class="form-group">
                <label class="font-size-13">Versão mínima — iOS</label>
                <input type="text" class="form-control" placeholder="ex: 0.4.21" v-model="brand.appVersion.minVersion.ios" />
              </div>
            </div>

            <div class="col-md-3">
              <div class="form-group">
                <label class="font-size-13">Versão mínima — Android</label>
                <input type="text" class="form-control" placeholder="ex: 0.4.21" v-model="brand.appVersion.minVersion.android" />
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label class="font-size-13">Bloqueando hoje</label>
                <p class="mb-0 pt-2 font-size-13">
                  iOS: <strong>{{effectiveMinVersion(brand, 'ios') || 'ninguém'}}</strong>
                  &nbsp;·&nbsp;
                  Android: <strong>{{effectiveMinVersion(brand, 'android') || 'ninguém'}}</strong>
                </p>
                <small class="text-danger" v-if="!brand.appVersion.minVersion.ios || !brand.appVersion.minVersion.android">
                  Campo em branco não desliga o bloqueio: vale o fallback
                  <code>CURRENT_APP_VERSION</code> ({{fallbackMinVersion || 'não configurado'}}) do servidor.
                </small>
              </div>
            </div>
          </div>

          <div class="alert alert-light border mb-4 font-size-13">
            <i class="mdi mdi-alert-outline text-warning mr-1"></i>
            Só suba a versão mínima depois que a build já estiver <strong>disponível para download</strong> nas duas lojas.
            Uma mínima acima do que está publicado trava o usuário sem ter o que baixar — e ele não tem como sair disso.
          </div>

          <hr class="mt-0" />

          <h6 class="text-uppercase text-muted font-size-11 mb-2">Aviso de versão nova</h6>

          <div class="row">
            <div class="col-md-3">
              <div class="form-group">
                <label class="font-size-13">Última versão — iOS</label>
                <input type="text" class="form-control" placeholder="ex: 0.4.21" v-model="brand.appVersion.latestVersion.ios" :disabled="brand.appVersion.autoUpdateLatest" />
                <small class="text-muted" v-if="brand.appVersion.autoUpdateLatest">Preenchida pela App Store</small>
              </div>
            </div>

            <div class="col-md-3">
              <div class="form-group">
                <label class="font-size-13">Última versão — Android</label>
                <input type="text" class="form-control" placeholder="ex: 0.4.21" v-model="brand.appVersion.latestVersion.android" />
                <small class="text-muted">Preencha ao publicar na Play</small>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label class="font-size-13">Busca automática</label>
                <b-form-checkbox v-model="brand.appVersion.autoUpdateLatest" switch class="mb-1">
                  Consultar a versão publicada na App Store
                </b-form-checkbox>
                <small class="text-muted">
                  A cada 3 horas, pelo Bundle ID. A Google Play não tem consulta pública equivalente.
                </small>
              </div>
            </div>
          </div>

          <hr />

          <h6 class="text-uppercase text-muted font-size-11 mb-2">Identificação nas lojas</h6>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="font-size-13">Bundle ID — iOS</label>
                <input type="text" class="form-control" placeholder="ex: br.com.calypso.b2b" v-model="brand.appVersion.iosBundleId" />
              </div>
            </div>

            <div class="col-md-4">
              <div class="form-group">
                <label class="font-size-13">Link da App Store</label>
                <input type="text" class="form-control" v-model="brand.appVersion.iosStoreUrl" />
                <small class="text-muted" v-if="!brand.appVersion.iosStoreUrl && brand.defaultStoreUrl">Em branco usa o padrão do app</small>
                <small class="text-warning" v-if="!brand.appVersion.iosStoreUrl && !brand.defaultStoreUrl">Sem padrão para esta marca — preencha</small>
              </div>
            </div>

            <div class="col-md-4">
              <div class="form-group">
                <label class="font-size-13">Link da Google Play</label>
                <input type="text" class="form-control" v-model="brand.appVersion.androidStoreUrl" />
                <small class="text-muted" v-if="!brand.appVersion.androidStoreUrl && brand.defaultStoreUrl">Em branco usa o padrão do app</small>
                <small class="text-warning" v-if="!brand.appVersion.androidStoreUrl && !brand.defaultStoreUrl">Sem padrão para esta marca — preencha</small>
              </div>
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-12">
              <button type="button" class="btn btn-primary btn-rounded mb-2" @click="saveAppVersion(brand)">
                <i class="mdi mdi-check mr-1"></i> Salvar {{brand.label}}
              </button>
            </div>
          </div>
        </fieldset>
      </div>
    </div>

  </Layout>
</template>
